/**
 * What of a page is safe to send, and what is not.
 *
 * Everything in this file is a subtraction. The browser hands us a URL, a
 * referrer and a screen, and each of the three carries more than we are willing
 * to put in a row next to a visitor id — even one that expires at midnight.
 */
import { MAX_HOST_LENGTH, MAX_PATH_LENGTH, sanitizeUtmValue } from '@panel/shared/analytics';
import { UTM_QUERY_PARAMS, type UtmWireKey } from '@panel/shared/tracker';
import { scrubPath } from '@panel/shared/utils';

/**
 * The page, as a path and nothing else.
 *
 * `scrubPath` is imported rather than reimplemented, and that is a deliberate
 * reversal of what this file's counterpart on the server used to describe. The
 * rule that decides whether `/orders/3f2504e0-…` is a route or a person now has
 * exactly one implementation, shared by the browser and by ingestion, so the
 * two cannot drift and the fixture table in
 * `packages/shared/src/utils/__fixtures__/scrub-cases.json` tests one thing
 * instead of hoping two agree. It survives the trip into this bundle because
 * `@panel/shared/utils` pulls in no runtime dependency — its only import of zod
 * is a type, which is erased.
 *
 * The server still runs the same rule again on arrival, and must: this path
 * takes no credential beyond a key that sits in public page source, and a copy
 * of this script built last year keeps running on a site we do not control
 * until its owner redeploys.
 *
 * ## The query string and the fragment are dropped, not shortened
 *
 * They are where a password-reset token, a session id and an e-mail address
 * live, and unlike a path segment there is no shape that tells a safe one from
 * a dangerous one — `?ref=newsletter` and `?token=…` are the same syntax. So
 * the whole thing goes, and it never reaches the path we send: it is not in
 * `location.pathname` to begin with, and `scrubPath` drops one anyway if a
 * caller hands this function a whole URL.
 *
 * {@link campaign} below reads five parameters BY NAME out of the same query
 * string. That is not a hole in this rule and the argument for it is made
 * there: this rule is about shape, and a name is not a shape.
 */
export function currentPath(loc: Location): string {
  return scrubPath(loc.pathname, MAX_PATH_LENGTH) || '/';
}

/**
 * The five UTM parameters, by name, or nothing.
 *
 * ## Why this is allowed to read a query string when nothing else is
 *
 * `currentPath` drops the query because there is no shape that separates
 * `?ref=newsletter` from `?token=…`. This function never looks at a shape: it
 * asks {@link UTM_QUERY_PARAMS} for five literal names and cannot see any
 * other parameter, whatever it is called and whatever it contains. A URL
 * carrying a reset token and a campaign yields the campaign and not one byte
 * of the token, because the token's name is not on the list.
 *
 * The values themselves are still free text off a public URL, so they go
 * through `sanitizeUtmValue` — the same function the ingest boundary runs
 * again on arrival, imported rather than reimplemented for the reason
 * `scrubPath` is: a rule with two homes is a rule that drifts, and the drift
 * here would be silent on both sides.
 *
 * ## What it still cannot do
 *
 * A campaign parameter says which link was clicked. It does not say who
 * clicked it, it is identical for everybody who clicked the same link, and it
 * is gone from the row at midnight with everything else. A per-click
 * identifier — `gclid`, `fbclid` — is the opposite of all three, which is why
 * the list has five names on it and not seven.
 */
export function campaign(loc: Location): Partial<Record<UtmWireKey, string>> | undefined {
  try {
    // Cheapest possible early exit: the great majority of page views have no
    // query string at all, and this runs inside somebody else's page load.
    const search = loc.search;
    if (!search || search.indexOf('utm_') < 0) return undefined;
    const params = new URLSearchParams(search);
    let out: Partial<Record<UtmWireKey, string>> | undefined;
    for (const name in UTM_QUERY_PARAMS) {
      const value = sanitizeUtmValue(params.get(name));
      if (value) (out ||= {})[UTM_QUERY_PARAMS[name as keyof typeof UTM_QUERY_PARAMS]] = value;
    }
    return out;
  } catch {
    // A malformed query string is no campaign. It is never worth an exception
    // in somebody else's page.
    return undefined;
  }
}

/**
 * The referrer's HOST, never the referrer.
 *
 * A full referrer is the other page's URL, which means it is the other site's
 * query string — and `https://mail.example/read?msg=…` or a search engine's
 * result URL with the query in it is somebody's private business arriving in
 * our database through a door we opened. The host answers the only question the
 * dashboard asks of it ("where did this traffic come from"), and answers it
 * completely.
 *
 * A same-host referrer is dropped rather than sent: it is internal navigation,
 * it is not a traffic source, and reporting it would make every site's biggest
 * referrer itself.
 */
export function referrerHost(doc: Document, loc: Location): string | undefined {
  try {
    const raw = doc.referrer;
    if (!raw) return undefined;
    const host = new URL(raw).hostname;
    if (!host || host === loc.hostname) return undefined;
    return host.slice(0, MAX_HOST_LENGTH);
  } catch {
    // An unparseable referrer is no referrer. It is never worth an exception in
    // somebody else's page.
    return undefined;
  }
}

/**
 * Mobile, tablet or desktop — bucketed HERE, so the width never travels.
 *
 * The brief asks for device as a reporting dimension, and the honest way to get
 * it is the viewport: user-agent strings lie about it constantly and Chrome is
 * in the middle of freezing them. But `innerWidth` is a real number with real
 * entropy — 1437 is a good deal more identifying than "desktop" — and a product
 * that argues it cannot follow anyone should not be shipping a measurement it
 * does not need at a precision it does not use. The dashboard renders three
 * buckets, so three buckets is what leaves the browser.
 *
 * The breakpoints are the conventional ones and they are the dashboard's, not
 * a device census: a 900px window on a desktop reads as a tablet, which is
 * correct for the question "what layout was this person served".
 */
export function viewportClass(win: Window): 'm' | 't' | 'd' | undefined {
  try {
    const width = win.innerWidth || win.document.documentElement.clientWidth;
    if (!width) return undefined;
    return width < 768 ? 'm' : width < 1024 ? 't' : 'd';
  } catch {
    return undefined;
  }
}
